"use client";

import { ChevronDown, LogOut, Settings, Undo2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";

import { Popover } from "@/components/ui-system";
import { cn } from "@/lib/utils/cn";
import { useAuth } from "@/modules/auth/hooks";
import type { AuthUser } from "@/modules/auth/types";

function initialsFromUser(user: AuthUser): string {
  const local = (user.email?.split("@")[0] || "user").trim();
  const parts = local.split(/[._\-+]/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : local.slice(0, 2)).toUpperCase();
}

export type UserAccountMenuProps = { user: AuthUser | null; className?: string };

export function UserAccountMenu({ user, className }: UserAccountMenuProps) {
  const navigate = useNavigate();
  const { logout, impersonation, exitImpersonation } = useAuth();
  const [pending, setPending] = useState(false);
  const [switchingBack, setSwitchingBack] = useState(false);
  if (!user) return null;

  return (
    <Popover
      className={className}
      placement="bottom-end"
      ariaLabel="Account menu"
      contentClassName="w-72 p-0"
      trigger={
        <span className="inline-flex h-10 items-center gap-ds-1 rounded-control border border-card-border bg-surface-primary p-1 pr-ds-2 text-foreground shadow-raised">
          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-accent/25 bg-accent-muted text-caption font-bold uppercase text-accent">{initialsFromUser(user)}</span>
          <ChevronDown className="h-4 w-4 text-muted" aria-hidden />
        </span>
      }
    >
      {(close) => (
        <>
          <div className="border-b border-card-border px-ds-4 py-ds-3"><p className="text-micro uppercase tracking-[0.08em] text-muted">Signed in</p><p className="mt-ds-1 break-all text-caption font-semibold">{user.email ?? "—"}</p>{user.role ? <p className="mt-ds-1 text-micro capitalize text-muted">{user.role.replace(/_/g, " ")}</p> : null}</div>
          <div className="p-ds-2">
            {impersonation ? <button type="button" disabled={switchingBack} className="flex min-h-9 w-full items-center gap-ds-2 rounded-control px-ds-3 text-caption text-status-warning hover:bg-status-warning-surface disabled:opacity-50" onClick={async () => { close(); setSwitchingBack(true); try { await exitImpersonation(); navigate("/overview", { replace: true }); } finally { setSwitchingBack(false); } }}><Undo2 className="h-4 w-4" />{switchingBack ? "Switching back…" : "Switch back to your account"}</button> : null}
            <Link to="/profile" onClick={close} className="flex min-h-9 items-center gap-ds-2 rounded-control px-ds-3 text-caption text-foreground hover:bg-surface-hover"><Settings className="h-4 w-4 text-muted" />Profile settings</Link>
            <button type="button" disabled={pending} className={cn("flex min-h-9 w-full items-center gap-ds-2 rounded-control px-ds-3 text-caption text-status-error hover:bg-status-error-surface disabled:opacity-50")} onClick={async () => { close(); setPending(true); try { await logout(); navigate("/login", { replace: true }); } finally { setPending(false); } }}><LogOut className="h-4 w-4" />{pending ? "Signing out…" : "Sign out"}</button>
          </div>
        </>
      )}
    </Popover>
  );
}
